import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client'
import { ChevronRight, Building2 } from 'lucide-react'

interface Company {
  id: string
  name: string
  created_at: string
  logo_url: string | null
}

const CompanySelection = () => {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('companies')
          .select('id, name, created_at, logo_url')
          .order('name', { ascending: true })
        if (fetchError) throw fetchError
        setCompanies(data || [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch companies')
      } finally {
        setLoading(false)
      }
    }
    fetchCompanies()
  }, [])

  const handleSelectCompany = (companyId: string) => {
    sessionStorage.setItem('selectedCompanyId', companyId)

    const inspectionType = sessionStorage.getItem('inspectionType')
    const ndtType = sessionStorage.getItem('ndtType')
    const loftType = sessionStorage.getItem('loftType')

    if (inspectionType === 'ndt' && ndtType?.toUpperCase() === 'ET') {
      setTimeout(() => navigate('/et-test', { replace: true }), 0)
    } else if (inspectionType === 'loft' && loftType === 'sammeliste') {
      navigate(`/samleliste/${companyId}`)
    } else {
      navigate('/equipment')
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="w-10 h-10 border-2 border-[#1E3A5F] border-t-transparent rounded-full animate-spin mb-3" />
        <span className="text-sm text-slate-400">Laster selskaper...</span>
      </div>
    )
  }

  return (
    <div className="min-h-full p-6" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <div className="max-w-2xl mx-auto">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 mb-4">
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        {companies.length === 0 ? (
          <div className="text-center py-16">
            <Building2 size={40} className="mx-auto text-slate-200 mb-3" />
            <p className="text-sm text-slate-400">Ingen selskaper funnet</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            {/* Table header */}
            <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                Selskap ({companies.length})
              </p>
            </div>

            {/* Rows */}
            {companies.map((company, idx) => (
              <button
                key={company.id}
                onClick={() => handleSelectCompany(company.id)}
                className={`w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 active:bg-slate-100 transition-colors text-left group ${
                  idx > 0 ? 'border-t border-slate-100' : ''
                }`}
              >
                {/* Logo / initial avatar */}
                <div className="w-10 h-8 shrink-0 flex items-center justify-center">
                  {company.logo_url ? (
                    <img
                      src={company.logo_url}
                      alt={company.name}
                      className="w-full h-full object-contain"
                      onError={e => {
                        // Replace broken image with initial fallback
                        const el = e.currentTarget
                        el.style.display = 'none'
                        const parent = el.parentElement!
                        parent.innerHTML = `<span class="text-xs font-semibold text-slate-400">${company.name.charAt(0).toUpperCase()}</span>`
                      }}
                    />
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      {company.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>

                <span className="flex-1 text-sm font-medium text-slate-700 group-hover:text-[#1E3A5F] transition-colors">
                  {company.name}
                </span>
                <ChevronRight size={14} className="text-slate-300 group-hover:text-[#1E3A5F] transition-colors shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default CompanySelection
